import styled from 'styled-components';
import { GRUNDFOS_BLUE, ATTENTION_BLUE, BLUE1, TEXT_PRIMARY, TEXT_SECONDARY, INPUT_BORDER } from '../../../constants/colors';
import { FONT_FAMILY, FONT_SIZE } from '../../../constants/fonts';
import { MEDIA } from '../../../constants/breakpoints';

export const FeedbackModal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  pointer-events: none;
`;

export const FeedbackModal__Dialog = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 16px;
  width: 450px;
  max-width: calc(100vw - 80px);
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 1px rgba(0, 0, 0, 0.1);
  animation: slideInCenter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  pointer-events: auto;

  @keyframes slideInCenter {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  ${MEDIA.MOBILE} {
    width: 90%;
    max-width: 500px;
  }
`;

export const FeedbackModal__Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${BLUE1};
`;

export const FeedbackModal__Title = styled.h3`
  margin: 0;
  color: ${GRUNDFOS_BLUE};
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.TITLE};
`;

export const FeedbackModal__CloseBtn = styled.button`
  background: rgba(0, 0, 0, 0.05);
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: ${TEXT_SECONDARY};
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    transform: scale(1.1);
  }
`;

export const FeedbackModal__Content = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

export const FeedbackModal__FormGroup = styled.div`
  margin-bottom: 20px;
`;

export const FeedbackModal__Label = styled.label`
  display: block;
  color: ${GRUNDFOS_BLUE};
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.MEDIUM};
  margin-bottom: 8px;
`;

export const FeedbackModal__Description = styled.p`
  color: ${TEXT_SECONDARY};
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: ${FONT_SIZE.SMALL};
  margin-top: -4px;
  margin-bottom: 8px;
`;

export const FeedbackModal__TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 14px 16px;
  border: 2px solid ${BLUE1};
  border-radius: 12px;
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: ${FONT_SIZE.MEDIUM};
  resize: vertical;
  color: ${TEXT_PRIMARY};
  background: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${GRUNDFOS_BLUE};
    box-shadow: 0 0 0 3px rgba(17, 73, 123, 0.1);
  }

  &:hover {
    border-color: ${INPUT_BORDER};
  }
`;

export const FeedbackModal__Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid ${BLUE1};
  border-radius: 12px;
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: ${FONT_SIZE.MEDIUM};
  background: white;
  cursor: pointer;
  color: ${TEXT_PRIMARY};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${GRUNDFOS_BLUE};
    box-shadow: 0 0 0 3px rgba(17, 73, 123, 0.1);
  }

  &:hover {
    border-color: ${INPUT_BORDER};
  }
`;

export const FeedbackModal__Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid ${BLUE1};
  border-radius: 12px;
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: ${FONT_SIZE.MEDIUM};
  color: ${TEXT_PRIMARY};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${GRUNDFOS_BLUE};
    box-shadow: 0 0 0 3px rgba(17, 73, 123, 0.1);
  }

  &:hover {
    border-color: ${INPUT_BORDER};
  }
`;

export const FeedbackModal__StarRating = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

export const FeedbackModal__Star = styled.button<{ $selected: boolean }>`
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: ${props => props.$selected ? '#FFD700' : '#D1D5DB'};
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  padding: 4px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));

  &:hover {
    transform: scale(1.3);
    color: ${props => props.$selected ? '#FFC700' : '#FCD34D'};
  }
`;

export const FeedbackModal__Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${BLUE1};
`;

export const FeedbackModal__Btn = styled.button<{ $primary?: boolean }>`
  padding: 12px 24px;
  border-radius: 12px;
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.MEDIUM};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ${props => props.$primary ? `
    background: ${GRUNDFOS_BLUE};
    color: white;
    border: none;
    box-shadow: 0 2px 8px rgba(17, 73, 123, 0.3);

    &:hover {
      background: ${ATTENTION_BLUE};
      transform: scale(1.02);
      box-shadow: 0 4px 16px rgba(17, 73, 123, 0.4);
    }

    &:active {
      transform: scale(0.98);
    }
  ` : `
    background: white;
    color: ${TEXT_PRIMARY};
    border: 2px solid ${BLUE1};

    &:hover {
      border-color: ${INPUT_BORDER};
      background: rgba(0, 0, 0, 0.02);
      transform: scale(1.02);
    }

    &:active {
      transform: scale(0.98);
    }
  `}
`;
